/**
 * 来場者ホーム画面の初期表示データ集約サービス
 * 従来5本（セッション復元・最新お知らせ・緊急お知らせ・公開企画・スタンプ状況）を
 * 1本の集約RPC(visitor_get_home_bootstrap)で取得し、DBラウンドトリップを削減する。
 * 集約RPCが本番未適用の場合（undefined function等）は、従来5本の並行取得へ自動フォールバックする。
 */

import { getSupabaseClient } from '../../../services/supabase/client';
import { AppError } from '../../../shared/utils/errorHandling';
import { logError, logWarn } from '../../../shared/utils/logger';
import { validateRequiredString } from '../../../shared/utils/validation';
import {
  invalidateVisitorHomeBootstrapCache,
  withVisitorHomeBootstrapCache,
} from './visitorHomeBootstrapCache';
import { visitorGetPublishedEmergencyNotices } from './visitorEmergencyNoticeService';
import { visitorSearchEvents } from './visitorEventService';
import { visitorGetPublishedNotices } from './visitorNoticeService';
import { visitorGetEventVisits } from './visitorStampService';
import { visitorRestoreSession } from './visitorAuthService';

export { invalidateVisitorHomeBootstrapCache } from './visitorHomeBootstrapCache';
export { VISITOR_HOME_BOOTSTRAP_CACHE_TTL_MS } from './visitorHomeBootstrapCache';

/** 集約RPC名 */
const VISITOR_GET_HOME_BOOTSTRAP_RPC_NAME = 'visitor_get_home_bootstrap';

/** 入力不正エラーコード */
export const VISITOR_HOME_BOOTSTRAP_INVALID_INPUT_CODE = 'VISITOR_HOME_BOOTSTRAP_INVALID_INPUT';

/** 取得失敗エラーコード */
export const VISITOR_HOME_BOOTSTRAP_FAILED_CODE = 'VISITOR_HOME_BOOTSTRAP_FAILED';

/** ホーム集約データの型 */
export type VisitorHomeBootstrapData = {
  session: {
    isValid: boolean;
    isLost: boolean;
    visitorId: string | null;
    nickname: string;
    currentPoint: number;
    maxReachedPoint: number;
    sessionExpiresAt: string;
  };
  notices: Array<{
    noticeId: string;
    title: string;
    body: string;
    targetRole: string;
    isImportant: boolean;
    publishedAt: string | null;
    closedAt: string | null;
  }>;
  emergencyNotices: Array<{
    noticeId: string;
    title: string;
    body: string;
    imageUrl: string;
    publishedAt: string | null;
  }>;
  events: Array<{
    eventId: string;
    title: string;
    description: string;
    location: string;
    category: string;
    startAt: string | null;
    endAt: string | null;
    searchKeywords: string[];
  }>;
  eventVisits: Array<{ eventId: string; claimedAt: string }>;
  stampRound: number;
  earnedIslandIds: string[];
};

/**
 * RPCエラーが「関数が本番に未適用」を示すか判定する
 * PostgRESTの関数未検出（PGRST202）または「Could not find the function」を対象にする。
 * schema cacheの一時的な再読込（PGRST002）は未適用ではないため、通常の通信失敗として扱う
 * @param {{ code?: string, message?: string } | null} rpcError - RPCエラー
 * @returns {boolean} 未適用エラーの場合true
 */
const isUndefinedFunctionError = (rpcError: { code?: string; message?: string } | null | undefined): boolean => {
  if (!rpcError) {
    return false;
  }

  if (rpcError.code === 'PGRST202') {
    return true;
  }

  const message = rpcError.message ?? '';
  return message.includes('Could not find the function');
};

/**
 * Storage画像URLを生成する（emergencyNoticeServiceと同じバケット規約）
 * @param {any} supabaseClient - Supabaseクライアント
 * @param {string | null | undefined} storagePath - ストレージパス
 * @returns {string} 公開URL（無ければ空文字）
 */
const toNoticeImageUrl = (supabaseClient, storagePath: string | null | undefined): string => {
  const path = String(storagePath ?? '').trim();

  if (!path) {
    return '';
  }

  const { data } = supabaseClient.storage.from('notice-images').getPublicUrl(path);
  return data?.publicUrl ?? '';
};

/**
 * 集約RPCのjsonb戻り値をアプリ向けの形式へ変換する
 * @param {any} rpcRow - RPC戻り値
 * @param {any} supabaseClient - Supabaseクライアント
 * @returns {VisitorHomeBootstrapData} 変換済みホーム集約データ
 * @throws {AppError} レスポンス形式が不正な場合
 */
const normalizeHomeBootstrapRpcRow = (rpcRow, supabaseClient): VisitorHomeBootstrapData => {
  const sessionRow = rpcRow?.session ?? null;

  if (!sessionRow) {
    throw new AppError('visitor_get_home_bootstrap のレスポンス形式が不正です。', {
      code: VISITOR_HOME_BOOTSTRAP_FAILED_CODE,
      statusCode: 500,
      details: { rpcRow },
    });
  }

  /** セッション復元結果（visitorRestoreSessionと同じ形状） */
  const session = {
    isValid: Boolean(sessionRow.sessionIsValid),
    isLost: Boolean(sessionRow.sessionLost),
    visitorId: sessionRow.visitorId ?? null,
    nickname: sessionRow.nickname ?? '',
    currentPoint: Number(sessionRow.currentPoint ?? 0),
    maxReachedPoint: Number(sessionRow.maxReachedPoint ?? 0),
    sessionExpiresAt: sessionRow.sessionExpiresAt ?? '',
  };

  const latestNoticeRow = rpcRow?.latestNotice ?? null;

  /** 最新お知らせ一覧（visitorGetPublishedNotices(1)と同じ形状） */
  const notices = latestNoticeRow
    ? [
      {
        noticeId: latestNoticeRow.noticeId,
        title: latestNoticeRow.title,
        body: latestNoticeRow.body,
        targetRole: latestNoticeRow.targetRole,
        isImportant: Boolean(latestNoticeRow.isImportant),
        publishedAt: latestNoticeRow.publishedAt ?? null,
        closedAt: latestNoticeRow.closedAt ?? null,
      },
    ]
    : [];

  /** 公開中の緊急お知らせ一覧（visitorGetPublishedEmergencyNoticesと同じ形状） */
  const emergencyNotices = Array.isArray(rpcRow?.emergencyNotices)
    ? rpcRow.emergencyNotices.map((row) => ({
      noticeId: String(row?.noticeId ?? ''),
      title: String(row?.title ?? ''),
      body: String(row?.body ?? ''),
      imageUrl: toNoticeImageUrl(supabaseClient, row?.imagePath),
      publishedAt: row?.publishedAt ?? null,
    }))
    : [];

  /** 公開企画一覧（visitorSearchEvents('')と同じ形状） */
  const events = Array.isArray(rpcRow?.events)
    ? rpcRow.events.map((row) => ({
      eventId: row?.eventId,
      title: row?.title,
      description: row?.description ?? '',
      location: row?.location ?? '',
      category: row?.category ?? '',
      startAt: row?.startAt ?? null,
      endAt: row?.endAt ?? null,
      searchKeywords: Array.isArray(row?.searchKeywords) ? row.searchKeywords : [],
    }))
    : [];

  /** スタンプ状況一覧（visitorGetEventVisitsと同じ形状） */
  const eventVisits = Array.isArray(rpcRow?.eventVisits)
    ? rpcRow.eventVisits.map((row) => ({
      eventId: row?.eventId,
      claimedAt: row?.claimedAt,
    }))
    : [];

  /** スタンプラリーの現在の周（周回のmigrationより前のDBは返さないので1周目） */
  const stampRound = typeof rpcRow?.stampRound === 'number' ? rpcRow.stampRound : 1;

  /** 称号を獲得済みの島ID（周をまたいで残る） */
  const earnedIslandIds = Array.isArray(rpcRow?.earnedIslandIds) ? rpcRow.earnedIslandIds : [];

  return {
    session,
    notices,
    emergencyNotices,
    events,
    eventVisits,
    stampRound,
    earnedIslandIds,
  };
};

/**
 * 集約RPCを実行する
 * @param {string} sessionToken - 来場者セッショントークン
 * @returns {Promise<VisitorHomeBootstrapData>} ホーム集約データ
 * @throws {AppError} 未適用エラー（isUndefinedFunctionError対象）を含むRPC失敗時
 */
const fetchHomeBootstrapViaRpc = async (sessionToken: string): Promise<VisitorHomeBootstrapData> => {
  const supabaseClient = getSupabaseClient();

  const { data, error } = await supabaseClient.rpc(VISITOR_GET_HOME_BOOTSTRAP_RPC_NAME, {
    p_session_token: sessionToken,
  });

  if (error) {
    if (isUndefinedFunctionError(error)) {
      // 呼び出し元でフォールバックへ切り替えるため、判定可能なエラーとしてそのままthrowする
      throw new AppError('visitor_get_home_bootstrap が本番へ未適用です。', {
        code: VISITOR_HOME_BOOTSTRAP_FAILED_CODE,
        statusCode: 500,
        cause: error,
        details: { code: error.code, message: error.message, isUndefinedFunction: true },
      });
    }

    logError('visitor_get_home_bootstrap RPC 実行に失敗しました。', {
      code: error.code,
      message: error.message,
      details: error.details,
    });

    throw new AppError('ホーム画面情報の取得に失敗しました。', {
      code: VISITOR_HOME_BOOTSTRAP_FAILED_CODE,
      statusCode: 500,
      details: { message: error.message },
    });
  }

  return normalizeHomeBootstrapRpcRow(data, supabaseClient);
};

/**
 * 従来5本の並行取得へフォールバックする
 * 集約RPCが本番未適用の場合でも来場者体験を壊さないための互換経路
 * @param {string} sessionToken - 来場者セッショントークン
 * @returns {Promise<VisitorHomeBootstrapData>} ホーム集約データ
 * @throws {AppError} いずれかの取得に失敗した場合（通信失敗を0件・0ptに丸めない）
 */
const fetchHomeBootstrapViaFallback = async (sessionToken: string): Promise<VisitorHomeBootstrapData> => {
  const [restoreResult, notices, emergencyNotices, events, eventVisits] = await Promise.all([
    visitorRestoreSession(sessionToken),
    visitorGetPublishedNotices(1),
    visitorGetPublishedEmergencyNotices(),
    visitorSearchEvents(''),
    visitorGetEventVisits(sessionToken),
  ]);

  return {
    session: {
      isValid: restoreResult.isValid,
      isLost: restoreResult.isLost,
      visitorId: restoreResult.visitorId,
      nickname: restoreResult.nickname,
      currentPoint: restoreResult.currentPoint,
      maxReachedPoint: restoreResult.maxReachedPoint,
      sessionExpiresAt: restoreResult.sessionExpiresAt,
    },
    notices,
    emergencyNotices,
    events,
    eventVisits,
    stampRound: 1,
    earnedIslandIds: [],
  };
};

/**
 * 来場者ホーム画面の初期表示データを取得する
 * TTLメモ化・in-flight dedupeにより、短時間の再フォーカスでは再取得しない
 * @param {string} sessionToken - 来場者セッショントークン
 * @param {{ forceRefresh?: boolean }} [options={}] - forceRefresh指定でキャッシュを無視する
 * @returns {Promise<VisitorHomeBootstrapData>} ホーム集約データ
 * @throws {AppError} 入力不正・通信失敗時（通信失敗を0件・0ptとして丸めない）
 */
export const visitorGetHomeBootstrap = async (
  sessionToken: string,
  options: { forceRefresh?: boolean } = {},
): Promise<VisitorHomeBootstrapData> => {
  const sessionTokenValidation = validateRequiredString(sessionToken, 'セッショントークン');

  if (!sessionTokenValidation.isValid) {
    throw new AppError(sessionTokenValidation.message, {
      code: VISITOR_HOME_BOOTSTRAP_INVALID_INPUT_CODE,
      statusCode: 400,
    });
  }

  const normalizedSessionToken = sessionToken.trim();

  return withVisitorHomeBootstrapCache(
    normalizedSessionToken,
    async () => {
      try {
        return await fetchHomeBootstrapViaRpc(normalizedSessionToken);
      } catch (rpcCallError) {
        const isUndefinedFunction = Boolean(
          rpcCallError instanceof AppError && rpcCallError.details?.isUndefinedFunction,
        );

        if (!isUndefinedFunction) {
          throw rpcCallError;
        }

        logWarn('visitor_get_home_bootstrap が本番未適用のため、従来5本の並行取得へフォールバックします。', {
          errorMessage: rpcCallError instanceof Error ? rpcCallError.message : String(rpcCallError),
        });

        return fetchHomeBootstrapViaFallback(normalizedSessionToken);
      }
    },
    { forceRefresh: options.forceRefresh },
  );
};
